import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  getWhatsAppPreviewChatPrompts,
  updateWhatsAppPreviewChatPrompts,
} from '../services/api';
import { getApiErrorMessage } from '../../../../utils/apiError';

const PAGE_BG = 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)';
const CARD_BORDER = '1px solid rgba(148, 163, 184, 0.22)';
const MAX_PROMPT_LENGTH = 20000;

const formatUpdatedAt = (value) => {
  if (!value) return 'Ainda não informado';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Ainda não informado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const WhatsAppPreviewPromptsPage = () => {
  const [customerPrompt, setCustomerPrompt] = useState('');
  const [foodstationPrompt, setFoodstationPrompt] = useState('');
  const [originalCustomerPrompt, setOriginalCustomerPrompt] = useState('');
  const [originalFoodstationPrompt, setOriginalFoodstationPrompt] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');

  const hasChanges = customerPrompt !== originalCustomerPrompt
    || foodstationPrompt !== originalFoodstationPrompt;
  const canSave = customerPrompt.trim() && foodstationPrompt.trim() && hasChanges && !saving;

  const applyPrompts = (data) => {
    const nextCustomer = String(data?.customerChatPrompt || '');
    const nextFoodstation = String(data?.foodstationChatPrompt || '');
    setCustomerPrompt(nextCustomer);
    setFoodstationPrompt(nextFoodstation);
    setOriginalCustomerPrompt(nextCustomer);
    setOriginalFoodstationPrompt(nextFoodstation);
    setUpdatedAt(data?.updatedAt || null);
  };

  const loadPrompts = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setSaveError('');
    try {
      applyPrompts(await getWhatsAppPreviewChatPrompts());
    } catch (requestError) {
      setLoadError(getApiErrorMessage(
        requestError,
        'Não foi possível carregar os prompts globais.',
      ));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const savePrompts = async () => {
    if (!customerPrompt.trim() || !foodstationPrompt.trim()) {
      setSaveError('Preencha os dois prompts globais antes de salvar.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      applyPrompts(await updateWhatsAppPreviewChatPrompts({
        customerChatPrompt: customerPrompt,
        foodstationChatPrompt: foodstationPrompt,
      }));
    } catch (requestError) {
      setSaveError(getApiErrorMessage(
        requestError,
        'Não foi possível salvar os prompts globais.',
      ));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100%', p: { xs: 1.5, sm: 2, md: 3 }, background: PAGE_BG }}>
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Box
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            border: CARD_BORDER,
            boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ md: 'flex-start' }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: '#ecfdf5', color: '#047857' }}>
                <ChatOutlinedIcon />
              </Avatar>
              <Box>
                <Typography component="h1" variant="h5" fontWeight={850} color="#0f172a">
                  Prompts globais do preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Usados nas conversas de WhatsApp de todos os prospectos, salvo quando um
                  prospecto tiver um prompt específico.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadPrompts}
                disabled={loading || saving}
              >
                Recarregar
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                onClick={savePrompts}
                disabled={loading || !canSave}
              >
                {saving ? 'Salvando...' : 'Salvar prompts'}
              </Button>
            </Stack>
          </Stack>

          <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
            O prompt do cliente é concatenado no servidor com os dados atuais do prospecto.
            O prompt da FoodStation é usado sozinho. Se o prospecto tiver um texto próprio,
            esse texto substitui o global correspondente.
          </Alert>

          {loadError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={<Button onClick={loadPrompts}>Tentar novamente</Button>}
            >
              {loadError}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : !loadError && (
            <Stack spacing={2.5} sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Última atualização: {formatUpdatedAt(updatedAt)}
                {hasChanges ? ' • alterações não salvas' : ''}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={10}
                label="Prompt global da conversa do cliente"
                value={customerPrompt}
                disabled={saving}
                helperText={`${customerPrompt.length.toLocaleString('pt-BR')} / ${MAX_PROMPT_LENGTH} caracteres. Será concatenado com os dados do estabelecimento.`}
                inputProps={{
                  maxLength: MAX_PROMPT_LENGTH,
                  'aria-label': 'Prompt global da conversa do cliente',
                }}
                onChange={(event) => setCustomerPrompt(event.target.value)}
              />
              <TextField
                fullWidth
                multiline
                minRows={10}
                label="Prompt global da conversa com a FoodStation"
                value={foodstationPrompt}
                disabled={saving}
                helperText={`${foodstationPrompt.length.toLocaleString('pt-BR')} / ${MAX_PROMPT_LENGTH} caracteres. Usado sozinho, sem concatenar os dados do prospecto.`}
                inputProps={{
                  maxLength: MAX_PROMPT_LENGTH,
                  'aria-label': 'Prompt global da conversa com a FoodStation',
                }}
                onChange={(event) => setFoodstationPrompt(event.target.value)}
              />
              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WhatsAppPreviewPromptsPage;
